/** Firebase Storage folder for splat PNG assets (flat list). */
export const SPLAT_STORAGE_FOLDER = 'splats';

/** Max file size for splat images (mobile-friendly). */
export const SPLAT_IMAGE_MAX_BYTES = 100 * 1024;

/** Required square dimensions (px). */
export const SPLAT_IMAGE_SIZE_PX = 220;

const FILENAME_SAFE = /^[a-z0-9._-]+$/;

/**
 * Basename only: strip path, lowercase, spaces → underscores, drop invalid chars.
 * Must end with .png and match safe charset.
 * @param {string} rawName
 * @returns {string | null} null if empty or invalid after sanitization
 */
export function sanitizeSplatImageFilename(rawName) {
  if (!rawName || typeof rawName !== 'string') return null;
  let base = rawName.trim().split(/[/\\]/).pop() || '';
  if (!base) return null;
  base = base.toLowerCase().replace(/\s+/g, '_');
  if (!base.endsWith('.png')) {
    return null;
  }
  if (!FILENAME_SAFE.test(base)) {
    return null;
  }
  const withoutExt = base.slice(0, -4);
  if (!withoutExt) {
    return null;
  }
  return base;
}

/**
 * @param {File} file
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
export async function validateSplatImageFile(file) {
  if (!file || !(file instanceof File)) {
    return { ok: false, message: 'Please choose a file to upload.' };
  }
  if (file.type !== 'image/png') {
    return {
      ok: false,
      message: 'Only PNG files are allowed. Save your image as a PNG and try again.',
    };
  }
  if (file.size > SPLAT_IMAGE_MAX_BYTES) {
    return {
      ok: false,
      message: `File is too large (max ${SPLAT_IMAGE_MAX_BYTES / 1024}KB). Try exporting a smaller PNG.`,
    };
  }

  try {
    const dims = await loadImageDimensions(file);
    if (
      dims.width !== SPLAT_IMAGE_SIZE_PX ||
      dims.height !== SPLAT_IMAGE_SIZE_PX
    ) {
      return {
        ok: false,
        message: `Image must be exactly ${SPLAT_IMAGE_SIZE_PX}×${SPLAT_IMAGE_SIZE_PX} pixels. Yours is ${dims.width}×${dims.height}.`,
      };
    }
    return { ok: true };
  } catch {
    return {
      ok: false,
      message: 'Could not read this image. Try a different PNG file.',
    };
  }
}

function loadImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

/**
 * Map Firestore `image` (filename) to a download URL from the Storage listing.
 * Storage names are lowercased on upload; Firestore may still use older casing.
 *
 * @param {Record<string, string>} imageUrlByName
 * @param {string} [imageName]
 * @returns {string | undefined}
 */
export function resolveStoredImageUrl(imageUrlByName, imageName) {
  const key = (imageName ?? '').trim();
  if (!key || !imageUrlByName) return undefined;
  if (imageUrlByName[key]) return imageUrlByName[key];
  const lower = key.toLowerCase();
  for (const [k, v] of Object.entries(imageUrlByName)) {
    if (k.toLowerCase() === lower) {
      return v;
    }
  }
  return undefined;
}
